'use client';

import { Container } from '@/components/utils/Container';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { FileSearch, UsersRound } from 'lucide-react';
import { SectionPanel } from '@/components/ui/design-system';
import { Button } from '@/components/ui/forms/button';
import { PublicPageHero } from '@/components/features/public';
import { FeaturePromoCard } from '@/components/ui/FeaturePromoCard';
import BriefContactsPreview from '@/app/brief/components/BriefContactsPreview';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { PaginatorBar } from '@/components/ui/navigation';
import { useRouter } from 'next/navigation';
import { useCompanySearch } from '@/hooks/useCompanySearch';
import { useSafeSearchParams } from '@/hooks/useSafeSearchParams';
import { Company } from '@/types/companies-types';
import { Alert } from '@/components/ui/feedback/alert';
import { CompanyQuickViewDialog } from '@/app/dashboard/components/company/CompanyQuickViewDialog';
import { CompaniesList } from '@/components/features/companies/CompaniesList';
import { CompanyLookupSearchPanel } from '@/components/features/companies/CompanyLookupSearchPanel';
import MainLoader from '@/components/ui/feedback/MainLoader';
import { paths } from '@/app/paths';
import { isSearchConfirmShortcut } from '@/lib/keyboard-shortcuts';
import { useSearchProgress } from '@/hooks/useSearchProgress';

const FEATURED_COMPANY_QUERIES = [
  'Amazon',
  'Apple',
  'Google',
  'Meta',
  'Microsoft',
  'Netflix',
  'NVIDIA',
  'Tesla',
  'Salesforce',
  'ServiceNow',
  'Snowflake',
  'Stripe',
  'Databricks',
  'Adobe',
  'Uber',
  'DoorDash'
];

const RISING_COMPANY_QUERIES = [
  'Rippling',
  'Anthropic',
  'Canva',
  'Figma',
  'Notion',
  'Airtable',
  'Gusto',
  'Flexport',
  'Klaviyo',
  'Scale AI',
  'Brex',
  'OpenAI'
];


const COMPANY_SIGNAL_FACTS = [
  {
    label: 'Signal score',
    value: '8.7/10',
    detail: 'Average buyer intent rating for warm accounts'
  },
  {
    label: 'Contact depth',
    value: '3.4',
    detail: 'Decision-makers surfaced per company search'
  },
  {
    label: 'Speed to intro',
    value: '48 hrs',
    detail: 'Typical turnaround for verified contacts'
  }
];

const MAX_LOCATION_FILTERS = 5;
const normalizeFilterValue = (value: string) => value.trim().replace(/\s+/g, ' ');
const normalizeForMatch = (value: string) => normalizeFilterValue(value).toLowerCase();

const buildCompaniesUrl = (params: {
  company?: string;
  locations?: string[];
  page?: number;
}) => {
  const urlParams = new URLSearchParams();
  if (params.company?.trim()) {
    urlParams.set('company', params.company.trim());
  }

  const normalizedLocations = (params.locations || [])
    .map((location) => normalizeFilterValue(location))
    .filter(Boolean);

  if (normalizedLocations.length > 0) {
    urlParams.set('locations', normalizedLocations.join(','));
  }

  if (params.page && params.page > 1) {
    urlParams.set('page', Math.floor(params.page).toString());
  }

  const queryString = urlParams.toString();
  return queryString ? `/companies?${queryString}` : '/companies';
};

const parseCompaniesSearchKeyFromUrl = (url: string): string => {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return '';
  }
  return url.slice(queryIndex + 1);
};

const getRandomItems = <T,>(array: T[], count: number): T[] => {
  if (array.length <= count) {
    return [...array];
  }
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
};

export default function CompaniesPage() {
  const router = useRouter();
  const urlSearchParams = useSafeSearchParams();
  const lastAppliedUrlKeyRef = useRef<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [locationInput, setLocationInput] = useState('');
  const [locationFilters, setLocationFilters] = useState<string[]>([]);
  const [hasSearchStarted, setHasSearchStarted] = useState(false);
  const [suggestedCompanies] = useState(() => getRandomItems(FEATURED_COMPANY_QUERIES, 12));
  const [emergingCompanies] = useState(() => getRandomItems(RISING_COMPANY_QUERIES, 8));
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const companiesPerPage = 10;

  const listTopRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);

  const { isLoading, error, performSearch, cancelSearch } = useCompanySearch({
    onSuccess: (data) => {
      setCompanies(data.data);
      setTotalResults(data.count);
    },
    onError: (searchError) => console.error('Search error:', searchError)
  });
  const searchProgress = useSearchProgress(isLoading);

  const urlState = useMemo(() => {
    const company = (urlSearchParams?.get('company') || urlSearchParams?.get('q') || '').trim();
    const pageRaw = Number(urlSearchParams?.get('page') || '1');
    const page = Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1;
    const locations = (urlSearchParams?.get('locations') || '')
      .split(',')
      .map((location) => normalizeFilterValue(location))
      .filter(Boolean);

    return {
      key: urlSearchParams?.toString() || '',
      company,
      locations,
      page,
    };
  }, [urlSearchParams]);

  useEffect(() => {
    if (lastAppliedUrlKeyRef.current === urlState.key) {
      return;
    }
    lastAppliedUrlKeyRef.current = urlState.key;

    setSearchQuery(urlState.company);
    setLocationFilters(urlState.locations);
    setCurrentPage(urlState.page);

    if (!urlState.company) {
      cancelSearch();
      setCompanies([]);
      setTotalResults(0);
      setHasSearchStarted(false);
      return;
    }

    setHasSearchStarted(true);
    void performSearch({
      company: urlState.company,
      limit: companiesPerPage,
      page: urlState.page,
      locations: urlState.locations,
    });
  }, [cancelSearch, companiesPerPage, performSearch, urlState]);

  useEffect(() => {
    if (!pendingScrollRef.current) return;
    if (isLoading) return;
    pendingScrollRef.current = false;
    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    });
  }, [isLoading]);

  const navigateToSearch = useCallback((query: string, page: number = 1, locations: string[] = locationFilters) => {
    const trimmedQuery = query.trim();
    const normalizedPage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
    const normalizedLocations = locations
      .map((location) => normalizeFilterValue(location))
      .filter(Boolean);

    const url = buildCompaniesUrl({
      company: trimmedQuery || undefined,
      page: normalizedPage,
      locations: normalizedLocations,
    });
    const searchKey = parseCompaniesSearchKeyFromUrl(url);

    // Keep URL-sync and search execution in one deterministic path.
    lastAppliedUrlKeyRef.current = searchKey;
    setSearchQuery(trimmedQuery);
    setLocationFilters(normalizedLocations);
    setCurrentPage(normalizedPage);

    if (!trimmedQuery) {
      cancelSearch();
      setCompanies([]);
      setTotalResults(0);
      setHasSearchStarted(false);
    } else {
      setHasSearchStarted(true);
      void performSearch({
        company: trimmedQuery,
        limit: companiesPerPage,
        page: normalizedPage,
        locations: normalizedLocations,
      });
    }

    router.replace(url, { scroll: false });
  }, [cancelSearch, companiesPerPage, locationFilters, performSearch, router]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    navigateToSearch(searchQuery, 1, locationFilters);
  };

  const handlePageChange = (page: number) => {
    pendingScrollRef.current = true;
    setCurrentPage(page);
    navigateToSearch(searchQuery, page, locationFilters);
  };

  const handleSuggestedSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    navigateToSearch(value, 1, locationFilters);
  };

  const handleLocationAdd = () => {
    const nextLocation = normalizeFilterValue(locationInput);
    if (!nextLocation) return;
    if (locationFilters.length >= MAX_LOCATION_FILTERS) return;
    if (locationFilters.some((value) => normalizeForMatch(value) === normalizeForMatch(nextLocation))) {
      return;
    }

    const updatedFilters = [...locationFilters, nextLocation];
    setLocationFilters(updatedFilters);
    setLocationInput('');
    setCurrentPage(1);

    if (searchQuery.trim()) {
      navigateToSearch(searchQuery, 1, updatedFilters);
    }
  };

  const handleLocationRemove = (location: string) => {
    const updatedFilters = locationFilters.filter((value) => value !== location);
    setLocationFilters(updatedFilters);
    setCurrentPage(1);

    if (searchQuery.trim()) {
      navigateToSearch(searchQuery, 1, updatedFilters);
    }
  };

  const handleLocationInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isSearchConfirmShortcut(event)) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLocationAdd();
    }
  };

  const handleLocationClear = () => {
    setLocationFilters([]);
    setCurrentPage(1);

    if (searchQuery.trim()) {
      navigateToSearch(searchQuery, 1, []);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalResults / companiesPerPage));

  const handleQuickView = (company: Company) => {
    setSelectedCompany(company);
    setIsQuickViewOpen(true);
  };

  return (
    <div className={`min-h-screen bg-background ${hasSearchStarted ? 'pt-20' : 'pt-28'}`}>
      {!hasSearchStarted && (
        <>
          <PublicPageHero
            eyebrow="Companies"
            headline="Company board with a"
            accent="direct line"
            description="Search any company, skip generic forms and info@ black holes, and surface the executive leaders or hiring managers who actually make decisions."
          />

          <Container className="relative z-10 max-w-5xl pt-8">
            <div className="grid gap-3 sm:grid-cols-2 mb-10">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-card border border-border border-t border-t-primary p-5"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  Traditional prospecting
                </p>
                <h3 className="text-base font-bold tracking-tight text-foreground mb-3">
                  Generic submission forms
                </h3>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  <li>• Wait weeks for a reply</li>
                  <li>• No idea who saw it</li>
                  <li>• Hope someone forwards it internally</li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-card border border-primary border-t border-t-primary p-5"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary mb-2">
                  CareerOffensive path
                </p>
                <h3 className="text-base font-bold tracking-tight text-foreground mb-3">
                  Direct decision-maker routes
                </h3>
                <ul className="space-y-1.5 text-sm text-foreground/85">
                  <li>• Identify the VP or director you need</li>
                  <li>• Grab verified email + LinkedIn instantly</li>
                  <li>• Write targeted messages tied to their needs</li>
                </ul>
              </motion.div>
            </div>
          </Container>
        </>
      )}

      <Container className="relative z-10 pb-20 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className={hasSearchStarted ? 'mb-3' : 'mb-12'}
        >
          <CompanyLookupSearchPanel
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            isSearching={isLoading}
            onCancelSearch={cancelSearch}
            searchProgress={searchProgress}
            locationInput={locationInput}
            onLocationInputChange={setLocationInput}
            onLocationInputKeyDown={handleLocationInputKeyDown}
            locationFilters={locationFilters}
            onAddLocation={handleLocationAdd}
            onRemoveLocation={handleLocationRemove}
            maxLocationFilters={MAX_LOCATION_FILTERS}
          />

          {!hasSearchStarted && (
            <div className="grid gap-3 sm:grid-cols-3 pt-5 mt-5 border-t border-border">
              {COMPANY_SIGNAL_FACTS.map((fact, index) => (
                <motion.div
                  key={fact.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.2 + index * 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="bg-muted/25 border border-border border-t border-t-primary p-3 sm:p-4"
                  style={{ boxShadow: 'var(--shadow-warm-sm)' }}
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    {fact.label}
                  </p>
                  <p className="text-xl font-extrabold mt-1.5 tracking-tight tabular-nums text-foreground">
                    {fact.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{fact.detail}</p>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>


        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-6"
          >
            <Alert className="border border-destructive/30 bg-destructive/10 text-destructive rounded-2xl shadow-lg">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center text-xs font-semibold">!</div>
                <div>
                  <p className="font-semibold">Search error</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            </Alert>
          </motion.div>
        )}

        {isLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <MainLoader title="Pulling company intel..." size="md" />
          </motion.div>
        )}

        {!searchQuery && !isLoading && !companies.length && !error && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-8 space-y-4"
          >
            <div className="bg-card border border-border p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4 justify-center">
                <BuildingOfficeIcon className="w-3.5 h-3.5 text-primary" />
                <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.18em]">
                  Break into these logos
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {suggestedCompanies.map((company) => (
                  <Button
                    key={company}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestedSearch(company)}
                  >
                    {company}
                  </Button>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4 justify-center">
                <SparklesIcon className="w-3.5 h-3.5 text-primary" />
                <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.18em]">
                  Emerging account list
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {emergingCompanies.map((company) => (
                  <Button
                    key={company}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSuggestedSearch(company)}
                    className="border border-dashed border-border"
                  >
                    {company}
                  </Button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {!isLoading && companies.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={hasSearchStarted ? 'mt-2 mb-12' : 'mt-4 mb-12'}
          >
            <div className="space-y-3">
              <div ref={listTopRef} className="scroll-mt-24" />
              <CompaniesList
                companies={companies}
                itemKeyPrefix="public-companies"
                onCardClick={(data) => {
                  const company = companies.find((c) => String(c.id) === data.id);
                  if (company) handleQuickView(company);
                }}
                onQuickView={(data) => {
                  const company = companies.find((c) => String(c.id) === data.id);
                  if (company) handleQuickView(company);
                }}
                renderFooter={(data) => {
                  const firstCompany = companies[0];
                  if (!firstCompany || String(firstCompany.id) !== data.id) {
                    return null;
                  }

                  return (
                    <BriefContactsPreview
                      companyName={data.name}
                      embedded
                      bare
                      groupedBlocksFlush
                    />
                  );
                }}
              />

              <PaginatorBar
                page={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isLoading}
              />
            </div>

            <SectionPanel
              title="Go Deeper on Your Targets"
              description="You found the companies. Now get the contacts and intel to reach out warm, not cold."
              contentClassName="p-4 sm:p-6"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <FeaturePromoCard
                  icon={FileSearch}
                  href={paths.reports()}
                  kicker="Decision-Maker Report"
                  kickerMeta="Target list + verified contacts"
                  headline="Run a report for your top companies"
                  subline="Rank 25 companies for your profile, then pull executive and hiring-manager contacts ready for outreach."
                  arrowLabel="Start a Decision-Maker Report"
                />
                <FeaturePromoCard
                  icon={UsersRound}
                  href={paths.contacts()}
                  kicker="Direct Contacts"
                  kickerMeta="Skip generic forms"
                  headline="Find decision-makers you can reach directly"
                  subline="Get names, titles, and contact channels for the people who make hiring decisions at each company."
                  arrowLabel="Browse decision-maker contacts"
                  animationDelay={0.1}
                />
              </div>
            </SectionPanel>
          </motion.div>
        )}

        {!isLoading && !error && searchQuery && companies.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <BuildingOfficeIcon className="w-10 h-10 text-muted-foreground/60 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-foreground mb-1">No companies found</h3>
            <p className="text-sm text-muted-foreground">
              Try a different spelling or search for a parent brand.
            </p>
          </motion.div>
        )}
      </Container>

      <CompanyQuickViewDialog
        open={isQuickViewOpen}
        onOpenChange={setIsQuickViewOpen}
        company={selectedCompany}
      />
    </div>
  );
}
